import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Observable, map, shareReplay, take, tap } from 'rxjs';
import { Review } from '../../models/Review';
import { ReviewService } from '../../services/review.service';
import { StateSnackBarService } from '../../services/state-snack-bar.service';
import { AuthService } from '../../services/auth.service';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AsyncPipe } from '@angular/common';
import { ReviewListComponent } from '../reststops/review-list/review-list.component';
import { RestStop } from '../../models/rest-stop';

@Component({
  selector: 'app-review-section',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    AsyncPipe,
    ReviewListComponent,
  ],
  templateUrl: './review-section.component.html',
  styleUrls: ['./review-section.component.scss'],
})
export class ReviewSectionComponent implements OnInit, OnChanges {
  @Input() restStopId!: number;
  @Input() restStop?: RestStop;
  @Input() showHeader: boolean = true;

  private reviewService = inject(ReviewService);
  private fb = inject(FormBuilder);
  private snackBarService = inject(StateSnackBarService);
  public authService = inject(AuthService);

  reviews$!: Observable<Review[]>;
  otherReviews$!: Observable<Review[]>;

  reviewForm: FormGroup = this.fb.group({
    rating: [0],
    comment: [''],
  });

  hoverRating = 0;
  private ownReviewId: number | null = null;
  public ownReviewStatus: 'approved' | 'pending' | 'none' = 'none';

  ngOnInit(): void {
    this.loadReviews();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['restStopId'] && !changes['restStopId'].firstChange) {
      this.loadReviews();
    }
  }

  private loadReviews(): void {
    if (!this.restStopId) return;
    const currentUserId = this.authService.currentUserValue?.username;

    this.reviews$ = this.reviewService.getReviews(this.restStopId).pipe(
      take(1),
      tap((reviews) => {
        const ownReview = reviews.find((r) => r.username === currentUserId);
        if (ownReview) {
          this.ownReviewId = ownReview.id;
          this.reviewForm.patchValue({
            rating: ownReview.rating,
            comment: ownReview.comment,
          });

          if (ownReview.approved) {
            this.ownReviewStatus = 'approved';
            this.reviewForm.enable();
          } else {
            this.ownReviewStatus = 'pending';
            this.reviewForm.disable();
          }
        } else {
          this.ownReviewStatus = 'none';
          this.reviewForm.enable();
        }
      }),
      shareReplay(1),
    );

    this.otherReviews$ = this.reviews$.pipe(map((reviews) => reviews.filter((r) => r.username !== currentUserId)));
  }

  setRating(rating: number): void {
    if (this.ownReviewStatus !== 'pending') {
      this.reviewForm.get('rating')?.setValue(rating);
    }
  }

  submitReview(): void {
    if ((this.reviewForm.get('rating')?.value || 0) < 1) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }

    if (this.reviewForm.invalid) {
      this.snackBarService.error({ message: 'Bitte gib eine Bewertung von 1-5 Sternen an.' });
      return;
    }

    const { rating, comment } = this.reviewForm.value;
    const request$ = this.ownReviewId
      ? this.reviewService.updateReview(this.restStopId, this.ownReviewId, rating, comment)
      : this.reviewService.postReview(this.restStopId, rating, comment);

    request$.subscribe({
      next: () => {
        const message = this.ownReviewId
          ? 'Deine geänderte Bewertung wird nach Prüfung freigeschaltet.'
          : 'Deine Bewertung wird nach Prüfung freigeschaltet.';
        this.snackBarService.success({ message: `Danke! ${message}` });
      },
      error: () => {
        this.snackBarService.error({ message: 'Fehler beim Senden der Bewertung.' });
      },
    });
  }
}
